/**
 * Step 1 Template (§16.1): pick one of the seven §5.1 mechanics (with outcome model, decision time and best-use
 * copy), then market policy, locale(s), currency, timezone and policy versions. The mechanic choice rewrites
 * `outcome` with a schema-valid block pointing at the first prize; feature agents refine it on the Rules step.
 */
import type { LotteryMechanic, OutcomeRules } from '@windrop/contracts';
import { useFormContext, useWatch } from 'react-hook-form';
import { RhfSelect, RhfText, fieldId } from '../../../components/FormField';
import {
  COMMON_TIMEZONES,
  MECHANIC_CATALOG,
  SUPPORTED_CURRENCY_OPTIONS,
  outcomeForMechanic,
  type DraftConfigInput,
} from './steps';

const BASE_MECHANICS = MECHANIC_CATALOG.filter((m) => m.mechanic !== 'FREE_GIVEAWAY');
/** Schema-valid ULID used only until the Prizes step adds a real prize (validation then flags `unknown_prize_ref`). */
const PLACEHOLDER_PRIZE_ID = '01ARZ3NDEKTSV4RRFFQ69G5FAV';

export function TemplateStep() {
  const { control, setValue, getValues } = useFormContext<DraftConfigInput>();
  const outcome = useWatch({ control, name: 'outcome' }) as OutcomeRules | undefined;
  const mechanic = outcome?.mechanic ?? 'SCHEDULED_RAFFLE';
  const baseMechanic = outcome?.mechanic === 'FREE_GIVEAWAY' ? outcome.baseMechanic.mechanic : undefined;

  const choose = (next: LotteryMechanic, base?: Exclude<LotteryMechanic, 'FREE_GIVEAWAY'>) => {
    const current = getValues('outcome') as OutcomeRules;
    const prize = getValues('prizes.prizes.0') as
      { prizeId?: string; value?: { amountMinor?: string } } | undefined;
    const prizeId = prize?.prizeId ?? PLACEHOLDER_PRIZE_ID;
    const rebuilt = outcomeForMechanic(current, next, prizeId, prize?.value?.amountMinor ?? '0', base);
    setValue('outcome', rebuilt as DraftConfigInput['outcome'], { shouldDirty: true, shouldValidate: true });
    if (next === 'FREE_GIVEAWAY') {
      setValue('entry.mode', 'FREE', { shouldDirty: true });
      setValue('entry.priceMinor', '0', { shouldDirty: true });
    }
  };

  return (
    <div className="wd-stack">
      <fieldset className="wdc-mechanics" id={fieldId('outcome.mechanic')} aria-describedby="mechanic-hint">
        <legend className="wd-field__label">Mechanic (required)</legend>
        <p id="mechanic-hint" className="wd-muted" style={{ gridColumn: '1 / -1', margin: 0 }}>
          The client never chooses a random winner: every mechanic is decided by the server and verifiable
          (§5.1). Segment sizes and animations are not live odds; the participant page links to the published
          odds basis.
        </p>
        {MECHANIC_CATALOG.map((m) => (
          <label key={m.mechanic} className="wdc-mechanic" data-testid={`mechanic-${m.mechanic}`}>
            <input
              type="radio"
              name="mechanic"
              value={m.mechanic}
              checked={mechanic === m.mechanic}
              onChange={() => choose(m.mechanic)}
            />
            <p className="wdc-mechanic__title">{m.name}</p>
            <dl>
              <dt>Outcome model</dt>
              <dd>{m.outcomeModel}</dd>
              <dt>Decision time</dt>
              <dd>{m.decisionTime}</dd>
              <dt>Best use</dt>
              <dd>{m.bestUse}</dd>
            </dl>
          </label>
        ))}
      </fieldset>
      {mechanic === 'FREE_GIVEAWAY' ? (
        <label className="wd-field" style={{ maxWidth: 360 }}>
          <span className="wd-field__label">Base mechanic for the giveaway (required)</span>
          <span className="wd-field__hint">
            Zero price, same caps, fairness and claims as the wrapped mechanic.
          </span>
          <select
            id={fieldId('outcome.baseMechanic.mechanic')}
            className="wd-select"
            value={baseMechanic ?? 'SCHEDULED_RAFFLE'}
            onChange={(e) =>
              choose('FREE_GIVEAWAY', e.target.value as Exclude<LotteryMechanic, 'FREE_GIVEAWAY'>)
            }
          >
            {BASE_MECHANICS.map((m) => (
              <option key={m.mechanic} value={m.mechanic}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <h2>Market and identity</h2>
      <div className="wdc-form-grid">
        <RhfText
          control={control}
          name="identity.slug"
          label="Slug"
          required
          hint="Participant URL: /l/<slug>. Lower-case letters, digits and hyphens."
          parse={(raw) => raw.toLowerCase().replace(/[^a-z0-9-]/g, '-')}
        />
        <RhfText
          control={control}
          name="identity.marketPolicyRef"
          label="Market policy"
          required
          hint="Policy reference that sets allowed countries, age and payment rules."
        />
        <RhfText
          control={control}
          name="identity.locales"
          label="Locales"
          required
          hint="BCP-47 tags, comma separated; the first is the default. Content must exist for each."
          format={(v) => (Array.isArray(v) ? v.join(', ') : String(v ?? ''))}
          parse={(raw) =>
            raw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          }
        />
        <RhfSelect
          control={control}
          name="identity.currency"
          label="Currency"
          required
          options={SUPPORTED_CURRENCY_OPTIONS}
          hint="ISO 4217; XTS is the demo currency."
        />
        <RhfText
          control={control}
          name="identity.timezone"
          label="Timezone"
          required
          hint="IANA zone used for every displayed time."
          list="wizard-timezones"
        />
        <datalist id="wizard-timezones">
          {COMMON_TIMEZONES.map((tz) => (
            <option key={tz} value={tz} />
          ))}
        </datalist>
        <RhfText control={control} name="identity.termsVersion" label="Terms version" required />
        <RhfText control={control} name="identity.privacyVersion" label="Privacy version" required />
        <RhfText
          control={control}
          name="identity.supportRoute"
          label="Support route"
          required
          hint="Path or URL participants reach from every status screen."
        />
      </div>
    </div>
  );
}
